import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import PublicHeader from '../../../components/ui/PublicHeader';
import PublicFooter from '../../../components/ui/PublicFooter';

const AboutPage = () => {
  const features = [
    {
      icon: "Cpu",
      title: "Precision AI Matching",
      description: "Our proprietary neural networks go beyond keywords, understanding the semantic context of experience to find the perfect cultural and technical fit."
    },
    {
      icon: "UserCheck",
      title: "Human-First Experience",
      description: "We bridge the gap between technology and talent. Every automated touchpoint is designed to feel personal, respectful, and transparent."
    },
    {
      icon: "TrendingUp",
      title: "Strategic Intelligence",
      description: "Transform raw recruitment data into actionable business intelligence. Predict hiring needs and optimize your talent pipeline with confidence."
    },
    {
      icon: "Fingerprint",
      title: "Ethical Integration",
      description: "Fairness is hardcoded into our systems. We utilize advanced debiasing protocols to ensure every candidate is evaluated solely on their merit."
    },
    {
      icon: "Zap",
      title: "Instant Velocity",
      description: "Eliminate bottlenecks in your hiring workflow. From screening to scheduling, we automate the mundane so you can focus on building relationships."
    },
    {
      icon: "ShieldCheck",
      title: "Enterprise Security",
      description: "Bank-grade security and strict compliance ensure that your sensitive recruitment data and candidate privacy are always protected."
    }
  ];

  const values = [
    {
      title: "Merit-Based Future",
      content: "We believe in a world where talent is the only currency that matters in recruitment."
    },
    {
      title: "Augmented Intelligence",
      content: "Our AI is built to empower recruiters, not replace them. It's the ultimate co-pilot for talent acquisition."
    },
    {
      title: "Radical Transparency",
      content: "No black boxes. We provide clear reasoning for every AI-driven insight, fostering trust at every step."
    }
  ];

  return (
    <div className="min-h-screen bg-[#fafafa] selection:bg-indigo-100 selection:text-indigo-900">
      <PublicHeader />

      {/* Hero Section with Glassmorphism */}
      <section className="relative overflow-hidden bg-[#003459] py-24 sm:py-32">
        {/* Background Decorative Elements */}
        <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/2 blur-3xl opacity-10 pointer-events-none">
          <div className="aspect-square w-[60rem] bg-gradient-to-br from-indigo-500 to-blue-500 rounded-full"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <span className="inline-block px-4 py-1.5 mb-6 text-sm font-semibold tracking-wide text-indigo-400 uppercase bg-indigo-500/10 border border-indigo-500/20 rounded-full">
              The Evolution of Talent Acquisition
            </span>
            <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-6xl mb-8 leading-tight">
              Where Technology Meets <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400">Human Potential</span>
            </h1>
            <p className="text-lg leading-8 text-gray-300 mb-10 max-w-xl mx-auto">
              We're redefining the recruitment lifecycle with sophisticated AI that understands the nuances of human experience, creating a faster, fairer way to build world-class teams.
            </p>
            <div className="flex items-center justify-center gap-x-6">
              <Link
                to="/public/jobs"
                className="rounded-full bg-white px-8 py-4 text-sm font-bold text-gray-900 shadow-xl hover:bg-gray-100 transition-all transform hover:-translate-y-0.5 active:scale-95"
              >
                Join the Platform
              </Link>
              <Link to="/contact" className="text-sm font-semibold leading-6 text-white group">
                Request a Demo <span aria-hidden="true" className="inline-block transition-transform group-hover:translate-x-1">→</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Values & Impact Section */}
      <section className="py-24 bg-white border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl mb-6">
                Our Core Philosophy
              </h2>
              <p className="text-lg text-gray-600 mb-12">
                We didn't just build a tool; we built a framework for ethical, high-velocity hiring. Our focus is on removing friction and bias, allowing the best talent to rise to the top.
              </p>

              <dl className="space-y-8">
                {values.map((value, i) => (
                  <div key={i} className="relative pl-12">
                    <dt className="text-lg font-bold text-gray-900 mb-1">
                      <div className="absolute left-0 top-1 flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600/10 text-indigo-600">
                        <Icon name="Check" className="h-5 w-5" />
                      </div>
                      {value.title}
                    </dt>
                    <dd className="text-base text-gray-600">{value.content}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="lg:pl-8">
              <div className="relative grid grid-cols-2 gap-4">
                {[
                  { label: "Efficiency Gain", val: "70%", color: "indigo" },
                  { label: "Candidate Net Promoter", val: "94", color: "blue" },
                  { label: "Diversity Increase", val: "40%", color: "pink" },
                  { label: "Time Saved/Recruiter", val: "25h", color: "emerald" }
                ].map((stat, i) => (
                  <div key={i} className="bg-gray-50 p-8 rounded-3xl border border-gray-100 flex flex-col items-center justify-center text-center group hover:bg-white hover:shadow-2xl hover:shadow-indigo-500/10 transition-all duration-300">
                    <span className={`text-4xl font-black text-${stat.color}-600 mb-2 truncate`}>{stat.val}</span>
                    <span className="text-sm font-medium text-gray-500 uppercase tracking-widest">{stat.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section - Premium Grid */}
      <section className="py-24 bg-[#fafafa]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl mb-4">
              Advanced Capability Suite
            </h2>
            <p className="text-lg text-gray-500 max-w-2xl mx-auto">
              Harness the power of industrial-grade artificial intelligence to orchestrate your entire recruitment engine.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="relative bg-white p-10 rounded-3xl border border-gray-100 shadow-sm transition-all duration-300 hover:shadow-xl hover:-translate-y-1 hover:border-indigo-100 group"
              >
                <div className="w-12 h-12 bg-gray-50 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-indigo-600 group-hover:text-white transition-colors duration-300">
                  <Icon name={feature.icon} className="h-6 w-6" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action - Modern Banner */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="relative bg-[#003459] rounded-[3rem] px-8 py-16 sm:px-16 sm:py-24 overflow-hidden shadow-3xl">
            <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/3 blur-3xl opacity-30 pointer-events-none">
              <div className="aspect-square w-[40rem] bg-white/20 rounded-full"></div>
            </div>

            <div className="relative z-10 text-center max-w-3xl mx-auto">
              <h2 className="text-4xl font-bold tracking-tight text-white mb-6">
                Ready to Upgrade Your Hiring Infrastructure?
              </h2>
              <p className="text-lg text-indigo-100 mb-10">
                Join the cohort of forward-thinking companies using our technology to identify and secure top-tier talent before the competition.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/public/register"
                  className="bg-white text-indigo-600 px-10 py-4 rounded-full font-bold shadow-lg hover:bg-gray-50 transition-all active:scale-95"
                >
                  Get Started Free
                </Link>
                <Link
                  to="/contact"
                  className="bg-slate-transparent text-white px-10 py-4 rounded-full font-bold border border-indigo-400/30 hover:bg-indigo-600 transition-all"
                >
                  Contact Expert
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default AboutPage;
